import * as React from 'react';
const { forwardRef, useState } = React;
const h = React.createElement;
import { icons } from '../icons';
import { inputSizeClasses } from '../shared';

export type InputSize = 'small' | 'middle' | 'large';

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'prefix'
> {
  size?: InputSize;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

export interface InputPasswordProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type'
> {
  visibilityToggle?: boolean;
}

export type InputTextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export interface InputSearchProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onSearch'
> {
  onSearch?: (value: string) => void;
  enterButton?: boolean | React.ReactNode;
}

type InputComponent = React.ForwardRefExoticComponent<
  InputProps & React.RefAttributes<HTMLInputElement>
> & {
  Password: React.ForwardRefExoticComponent<
    InputPasswordProps & React.RefAttributes<HTMLInputElement>
  >;
  TextArea: React.ForwardRefExoticComponent<
    InputTextAreaProps & React.RefAttributes<HTMLTextAreaElement>
  >;
  Search: React.ForwardRefExoticComponent<InputSearchProps & React.RefAttributes<HTMLInputElement>>;
};

const InputBase = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const { size = 'middle', prefix, suffix, className = '', disabled, ...rest } = props;
  const sizeClass = inputSizeClasses[size] || inputSizeClasses.middle;
  return h(
    'div',
    { className: 'relative' },
    prefix &&
      h(
        'span',
        {
          className: 'absolute left-3 top-1/2 -translate-y-1/2 text-dark-400 pointer-events-none',
        },
        prefix,
      ),
    h('input', {
      ref,
      className: `w-full bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-colors ${sizeClass} ${prefix ? 'pl-10' : ''} ${suffix ? 'pr-10' : ''} ${disabled ? 'opacity-50 cursor-not-allowed bg-dark-900' : ''} ${className}`,
      disabled,
      ...rest,
    }),
    suffix &&
      h(
        'span',
        {
          className: 'absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 pointer-events-none',
        },
        suffix,
      ),
  );
}) as InputComponent;

InputBase.Password = forwardRef<HTMLInputElement, InputPasswordProps>(
  function InputPassword(props, ref) {
    const { visibilityToggle = true, ...rest } = props;
    const [visible, setVisible] = useState(false);
    return h(
      'div',
      { className: 'relative' },
      h('input', {
        ref,
        type: visible ? 'text' : 'password',
        className:
          'w-full bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-colors px-4 py-2 text-sm pr-10',
        ...rest,
      }),
      visibilityToggle &&
        h(
          'span',
          {
            className:
              'absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 cursor-pointer hover:text-dark-200 transition-colors',
            onClick: () => setVisible(!visible),
          },
          visible ? h(icons.EyeOutlined) : h(icons.EyeInvisibleOutlined),
        ),
    );
  },
);

InputBase.TextArea = forwardRef<HTMLTextAreaElement, InputTextAreaProps>(
  function TextArea(props, ref) {
    const { className = '', ...rest } = props;
    return h('textarea', {
      ref,
      className: `w-full bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-colors px-4 py-2 text-sm ${className}`,
      ...rest,
    });
  },
);

InputBase.Search = forwardRef<HTMLInputElement, InputSearchProps>(function InputSearch(props, ref) {
  const { onSearch, enterButton: _enterButton, ...rest } = props;
  return h(
    'div',
    { className: 'relative' },
    h('input', {
      ref,
      className:
        'w-full bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-colors px-4 py-2 text-sm pr-10',
      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && onSearch) onSearch((e.target as HTMLInputElement).value);
      },
      ...rest,
    }),
    h(
      'span',
      {
        className:
          'absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 cursor-pointer hover:text-primary-400 transition-colors',
        onClick: () => {
          if (onSearch) onSearch(String(rest.value ?? ''));
        },
      },
      h(icons.SearchOutlined),
    ),
  );
});

const Input = InputBase;

export interface InputNumberProps {
  min?: number;
  max?: number;
  value?: number | string;
  onChange?: (value: number | undefined) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  className?: string;
  step?: number;
}

const InputNumber = forwardRef<HTMLInputElement, InputNumberProps>(
  function InputNumber(props, ref) {
    const {
      min,
      max,
      value,
      onChange,
      placeholder,
      style,
      disabled,
      className = '',
      step = 1,
    } = props;
    const [internalValue, setInternalValue] = useState<number | string>(
      value !== undefined ? value : '',
    );
    const currentValue = value !== undefined ? value : internalValue;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (val === '' || val === '-') {
        setInternalValue(val);
        if (onChange) onChange(undefined);
        return;
      }
      const num = Number(val);
      if (!isNaN(num)) {
        if (min !== undefined && num < min) return;
        if (max !== undefined && num > max) return;
        setInternalValue(num);
        if (onChange) onChange(num);
      }
    };

    const increment = () => {
      const num = Number(currentValue) || 0;
      const next = Math.min(max !== undefined ? max : Infinity, num + step);
      setInternalValue(next);
      if (onChange) onChange(next);
    };
    const decrement = () => {
      const num = Number(currentValue) || 0;
      const next = Math.max(min !== undefined ? min : -Infinity, num - step);
      setInternalValue(next);
      if (onChange) onChange(next);
    };

    return h(
      'div',
      {
        className: `inline-flex items-center bg-dark-800 border border-dark-700 rounded-lg overflow-hidden ${className}`,
        style,
      },
      h(
        'button',
        {
          type: 'button',
          className:
            'px-2 py-2 text-dark-400 hover:text-white hover:bg-dark-700 transition-colors border-r border-dark-700',
          onClick: decrement,
          disabled,
        },
        h(icons.MinusOutlined, { size: '0.75em' }),
      ),
      h('input', {
        ref,
        type: 'number',
        value: currentValue,
        onChange: handleChange,
        placeholder,
        disabled,
        className:
          'w-full bg-transparent text-white text-center text-sm focus:outline-none py-2 min-w-[60px]',
        min,
        max,
        step,
      }),
      h(
        'button',
        {
          type: 'button',
          className:
            'px-2 py-2 text-dark-400 hover:text-white hover:bg-dark-700 transition-colors border-l border-dark-700',
          onClick: increment,
          disabled,
        },
        h(icons.PlusOutlined, { size: '0.75em' }),
      ),
    );
  },
);

export { Input, InputNumber };
